import { Injectable } from '@angular/core';

import { NEVER, Observable, from, of } from 'rxjs';

import { HttpStatusCodesEnum, UserModel } from '@spa/api';

import { environment } from 'src/environments/environment';

const BFF_SESSION_CLAIM = 'bff:session_expires_in';
const USER_CACHE_KEY = 'bff_fund.user';

@Injectable({ providedIn: 'root' })
export class BffAuthService {
  private cachedUser: UserModel | null = null;

  private mapClaimsToUser(claims: Array<{ type: string; value: any }>): UserModel {
    const get = (type: string) => claims.find(c => c.type === type)?.value ?? null;

    const toBool = (v: any) => v === true || v === 'true';

    // Prefer the explicit two_factor_enabled claim; fall back to amr=mfa
    // (session authenticated with 2FA) when the BFF omits the claim. Either
    // way the flag only updates on the next login after enabling/disabling.
    const twoFactorClaim = get('two_factor_enabled');
    const twoFactorEnabled = twoFactorClaim !== null
      ? toBool(twoFactorClaim)
      : claims.some(c => c.type === 'amr' && c.value === 'mfa');

    return {
      id: get('sub'),
      userRelationType: Number(get('user_relation')),
      phoneNumber: get('phone_number'),
      phoneNumberVerified: toBool(get('phone_number_verified')),
      twoFactorEnabled,
      email: get('email'),
    } as unknown as UserModel;
  }

  getAuthenticatedUser(): UserModel | null {
    if (this.cachedUser) {
      return this.cachedUser;
    }

    const raw = localStorage.getItem(USER_CACHE_KEY);
    if (raw) {
      try {
        this.cachedUser = JSON.parse(raw) as UserModel;
        return this.cachedUser;
      } catch {
        localStorage.removeItem(USER_CACHE_KEY);
      }
    }

    return null;
  }

  isUserAuthenticated(): Observable<any> {
    return from(
      fetch(environment.bffUserUrl, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF': '1' },
      })
        .then(async response => {
          if (!response.ok) {
            this.clearCache();
            // Return Ok+false so AuthGuardService redirects to login instead of error page
            return { status: HttpStatusCodesEnum.Ok, data: false, messages: [] };
          }

          const claims: Array<{ type: string; value: any }> = await response.json();
          const valid = claims.some(c => c.type === BFF_SESSION_CLAIM);

          if (valid) {
            const user = this.mapClaimsToUser(claims);
            this.cachedUser = user;
            localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
          } else {
            this.clearCache();
          }

          return {
            status: valid ? HttpStatusCodesEnum.Ok : HttpStatusCodesEnum.Unauthorized,
            data: valid,
            messages: [],
          };
        })
        .catch(() => ({
          status: HttpStatusCodesEnum.Ok,
          data: false,
          messages: [],
        }))
    );
  }

  loginRedirect(_forceLogout?: boolean): Observable<never> {
    const isLoginTrigger = /\/authentication(\/login)?\/?$/.test(window.location.pathname);
    const returnUrl = isLoginTrigger ? window.location.origin : window.location.href;

    window.location.href = `${environment.bffLoginUrl}?returnUrl=${encodeURIComponent(returnUrl)}`;
    return NEVER;
  }

  completeLoginRedirect(): Observable<any> {
    return this.isUserAuthenticated();
  }

  /**
   * Re-establishes the BFF session at the given URL so that identity claims changed
   * server-side after login are reissued. The session's claims are fixed at login, so a
   * client whose phone number was just confirmed keeps reporting 'phone_number_verified'
   * as false until the session is renewed. The identity server's SSO session is still
   * alive, so this round-trips without asking for credentials again.
   */
  refreshSessionRedirect(returnUrl: string): Observable<never> {
    this.clearCache();

    const loginUrl = new URL(environment.bffLoginUrl);
    loginUrl.searchParams.set('returnUrl', new URL(returnUrl, window.location.origin).toString());

    window.location.href = loginUrl.toString();

    return NEVER;
  }

  getAwaitLogin(): boolean {
    return false;
  }

  logoutRedirect(): Observable<never> {
    this.clearCache();

    // Logout must be a top-level navigation, not a background fetch: the BFF
    // answers with a redirect chain through the identity server's end-session
    // endpoint, and only the browser can follow it with each domain's cookies.
    // A fetch stops at the first hop and leaves the SSO session alive.
    const logoutUrl = new URL(environment.bffLogoutUrl);
    logoutUrl.searchParams.set('returnUrl', window.location.origin);

    // Registering an unload handler opts this document out of the
    // back/forward cache, so the logged-out page is destroyed once the
    // navigation commits instead of lingering in memory.
    window.addEventListener('unload', () => {});
    window.location.href = logoutUrl.toString();

    return NEVER;
  }

  removeAuthenticatedUser(): Observable<any> {
    this.clearCache();
    return of({ status: HttpStatusCodesEnum.Ok, data: true, messages: [] });
  }

  redeemSignup(username: string, signupToken: string): Observable<any> {
    return from(
      fetch(environment.bffOnboardingRedeemSignupUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF': '1' },
        body: JSON.stringify({ username, signupToken }),
      })
        .then(response => ({
          status: response.ok ? HttpStatusCodesEnum.Ok : HttpStatusCodesEnum.BadRequest,
          data: response.ok,
          messages: [],
        }))
        .catch(() => ({ status: HttpStatusCodesEnum.BadRequest, data: false, messages: [] }))
    );
  }

  private clearCache(): void {
    this.cachedUser = null;
    localStorage.removeItem(USER_CACHE_KEY);
  }
}
